/**
 * Notification channel target -- the client half of the write-only target rule (OP#1917).
 *
 * The server never returns a channel's target (a webhook/Slack URL is the credential); it sends
 * `target_masked` for display and `has_target`. The editor therefore shows the mask as plain
 * text with an empty "replace" field, and must never put the mask into the request: an update
 * with no replacement omits `target` entirely so the server keeps the stored one. The server
 * also rejects a target carrying the mask marker (400), so this is UX, not the control.
 *
 * Pure functions, no React -- unit-tested by channelTarget.test.ts (`npm run test:unit`).
 */

/** The marker the server puts in every masked target (U+2026). Never valid in a real target. */
export const MASK_MARKER = "…";

/** Look-alikes of the marker (U+22EF, U+2025): never valid in a target either (OP#1921). */
const MASK_LOOKALIKES = [MASK_MARKER, "\u22ef", "\u2025"];

/**
 * An ellipsis typed by hand ("...", "···") right after the host or after the
 * host's "/": exactly where the server puts its mask ellipsis, so it is a re-typed mask. A "..."
 * deeper in a real path is NOT matched; the server has the full rule
 * (models/notification.ellipsis_after_prefix / resembles_mask).
 */
const RETYPED_AFTER_HOST = /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*?(?:\.{2,}|[\u00b7\u2027\u2219\u22c5\u30fb]{2,})(?:[/?#]|$)|^[a-z][a-z0-9+.-]*:\/\/[^/?#]*\/(?:\.{2,}|[\u00b7\u2027\u2219\u22c5\u30fb]{2,})/i;

/** True when a value looks like the server's display mask (pasted back, or re-typed). */
export function looksMasked(value: string | null | undefined): boolean {
  const v = (value ?? "").normalize("NFKC");
  return MASK_LOOKALIKES.some((m) => (value ?? "").includes(m)) || RETYPED_AFTER_HOST.test(v);
}

export interface ChannelUpdateInput {
  name: string;
  /** What the operator typed in the "Replace target" field ("" = keep the stored one). */
  replaceTarget: string;
  enabled: boolean;
}

/**
 * The PATCH body for an edit. `target` is present ONLY when a replacement was typed, so an edit
 * that only renames or toggles a channel never touches (or clears) the stored target.
 */
export function channelUpdateBody(input: ChannelUpdateInput): {
  name: string;
  enabled: boolean;
  target?: string;
} {
  const body: { name: string; enabled: boolean; target?: string } = {
    name: input.name.trim(),
    enabled: input.enabled,
  };
  const replacement = input.replaceTarget.trim();
  if (replacement) body.target = replacement;
  return body;
}

/**
 * Why the dialog's Save is blocked, or null when it may submit.
 * Create needs a target; edit needs none (keep), but a typed mask is refused like the server does.
 */
export function channelFormError(opts: {
  editing: boolean;
  name: string;
  target: string;
}): string | null {
  if (!opts.name.trim()) return "Name is required.";
  const t = opts.target.trim();
  if (!opts.editing && !t) return "Target is required.";
  if (t && looksMasked(t)) {
    return "That is the masked value. Paste the full new target, or leave the field empty to keep the current one.";
  }
  return null;
}
